import clsx from 'clsx'
import React, { useEffect, useRef } from 'react'
import { Control, Controller, RegisterOptions } from 'react-hook-form'
import styles from './text-area.module.css'

export interface TextAreaProps {
	label?: string
	name: string
	rows?: number
	maxLength?: number
	disabled?: boolean
	placeholder?: string
	className?: string
	error?: string
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	control?: Control<any>
	registerOptions?: RegisterOptions
	displayAsRequired?: boolean
	childElementsClasses?: {
		wrapper?: string
		textarea?: string
		label?: string
	}
	isAnimated?: boolean
}

export const TextArea: React.FC<TextAreaProps> = ({
	className,
	childElementsClasses,
	label,
	rows = 3,
	registerOptions,
	name,
	control,
	maxLength,
	disabled = false,
	error,
	placeholder,
}) => {
	const classes = clsx(
		styles.textArea,
		className,
		childElementsClasses?.wrapper,
		error && styles.error,
		disabled && styles.disabled
	)

	const textAreaRef = useRef<HTMLTextAreaElement>(null)

	useEffect(() => {
		const textArea = textAreaRef.current
		if (textArea) {
			const updateHeight = () => {
				textArea.style.height = 'auto'
				textArea.style.height = textArea.scrollHeight + 'px'
			}
			textArea.addEventListener('input', updateHeight)
			// Initial height update
			updateHeight()
			return () => {
				// Cleanup on unmount
				textArea.removeEventListener('input', updateHeight)
			}
		}
	}, [])

	if (!control) {
		// Fallback for when not using react-hook-form
		return (
			<div className={classes}>
				{label && (
					<label className={clsx(styles.label, childElementsClasses?.label)} htmlFor={`${name}`}>
						{label}
					</label>
				)}

				<textarea
					id={`${name}`}
					className={clsx(styles.input, childElementsClasses?.textarea)}
					rows={rows}
					maxLength={maxLength}
					disabled={disabled}
					placeholder={placeholder}
					ref={textAreaRef}
				/>

				<div className={styles.messageContainer}>
					{error && <div className={styles.errorMessage}>{error}</div>}
				</div>
			</div>
		)
	}

	return (
		<div className={classes}>
			<label className={clsx(styles.label, childElementsClasses?.label)} htmlFor={`${name}`}>
				{label}
			</label>

			<Controller
				name={name}
				control={control}
				disabled={disabled}
				rules={registerOptions}
				render={({ field: { ...fieldProps } }) => (
					<textarea
						{...fieldProps}
						id={`${name}`}
						className={clsx(styles.input, childElementsClasses?.textarea)}
						rows={rows}
						maxLength={maxLength}
						disabled={disabled}
						ref={textAreaRef}
						placeholder={placeholder}
					/>
				)}
			/>

			<div className={styles.messageContainer}>{error && <div className={styles.errorMessage}>{error}</div>}</div>
		</div>
	)
}
